import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import PageHeader from '../components/ui/PageHeader';
import { userFacingError } from '../utils/userFacingError';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const EinblendungJetztPage: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [selectedImage, setSelectedImage] = useState<HTMLImageElement | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGenerated, setIsGenerated] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  // Global Paste Event Listener
  useEffect(() => {
    const handleGlobalPaste = (event: ClipboardEvent) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            loadImageFromFile(file);
            event.preventDefault();
            return;
          }
        }
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => document.removeEventListener('paste', handleGlobalPaste);
  }, []);

  // Bild aus Datei laden
  const loadImageFromFile = (file: File) => {
    // Dateityp validieren
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle eine gültige Bilddatei aus');
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      setSelectedImage(img);
      setIsDownloaded(false);
      setError(null);
      setIsGenerated(false);
      toast.success('Bild erfolgreich geladen');
      // Automatisch generieren nach dem Laden
      setTimeout(() => generateEinblendung(img), 100);
    };
    
    img.onerror = () => {
      setError('Fehler beim Laden des Bildes');
      toast.error('Fehler beim Laden des Bildes');
    };
    
    img.src = URL.createObjectURL(file);
  };

  // Bild auswählen
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    loadImageFromFile(file);
  };

  // Paste Event Handler (Strg+V)
  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          loadImageFromFile(file);
          event.preventDefault();
          return;
        }
      }
    }
  };

  // Drag & Drop Event Handler
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      loadImageFromFile(files[0]);
    }
  };

  // Einblendung generieren
  const generateEinblendung = useCallback(async (imageToUse?: HTMLImageElement) => {
    const image = imageToUse || selectedImage;
    if (!image) {
      toast.error('Bitte wähle zuerst ein Bild aus');
      return;
    }

    try {
      setIsGenerating(true);
      setError(null);

      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas nicht gefunden');
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        throw new Error('Canvas-Kontext nicht verfügbar');
      }

      // Canvas-Dimensionen setzen (1920x1080)
      canvas.width = 1920;
      canvas.height = 1080;

      // Transparenten Hintergrund erstellen
      ctx.clearRect(0, 0, 1920, 1080);

      // Bildbereich definieren
      const targetX = 1267;
      const targetY = 186;
      const targetWidth = 653; // 1920 - 1267
      const maxHeight = 762;

      // Bild skalieren
      const aspectRatio = image.width / image.height;
      let scaledWidth = targetWidth;
      let scaledHeight = targetWidth / aspectRatio;

      // Prüfen ob Höhe zu groß ist
      if (scaledHeight > maxHeight) {
        scaledHeight = maxHeight;
        scaledWidth = maxHeight * aspectRatio;
      }

      // Bild horizontal zentriert, aber immer bei y=186 positionieren
      const centerX = targetX + (targetWidth - scaledWidth) / 2;
      const centerY = targetY; // Immer bei y=186, nicht zentriert

      // Bild zeichnen
      ctx.drawImage(image, centerX, centerY, scaledWidth, scaledHeight);

      setIsGenerated(true);
      toast.success('Einblendung erfolgreich generiert!');
    } catch (err) {
      const errorMessage = userFacingError(err, 'Unbekannter Fehler');
      setError(errorMessage);
      toast.error(`Fehler beim Generieren: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  }, [selectedImage]);

  // Einblendung herunterladen
  const downloadEinblendung = async () => {
    if (!canvasRef.current) {
      toast.error('Kein Bild zum Herunterladen verfügbar');
      return;
    }

    try {
      setIsDownloading(true);
      
      // Canvas als PNG mit transparentem Hintergrund konvertieren
      const dataURL = canvasRef.current.toDataURL('image/png');
      
      // Zeitstempel mit Sekunden erstellen
      const now = new Date();
      const timestamp = now.toISOString().replace(/[-:]/g, '_').replace(/\..+/, '').replace('T', '_');
      
      // Download-Link erstellen
      const link = document.createElement('a');
      link.download = `einblendung_jetzt_${timestamp}.png`;
      link.href = dataURL;
      
      // Download starten
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setIsDownloaded(true);
      toast.success('Einblendung heruntergeladen!');
    } catch (error) {
      console.error('Download-Fehler:', error);
      toast.error('Fehler beim Herunterladen');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <PageHeader title={<>"Einblendung Jetzt!"-Generator</>} description={<>Erstelle Einblendungen mit transparentem Hintergrund (1920x1080px)</>} />
        </div>
        
        <div className="px-6 py-8">
          <div className="space-y-6">
            <UnsavedChangesNotice dirty={!!selectedImage && !isDownloaded} busy={isGenerating || isDownloading} localOnly />
            {/* Bildauswahl */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Bild auswählen
              </h2>
              
              <input
                ref={fileInputRef}
                aria-label="Bilddatei auswählen"
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
              
              {/* Drag & Drop Zone */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragOver 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onPaste={handlePaste}
                tabIndex={0}
              >
                <div className="space-y-4">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  
                  <div>
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      Bild hierher ziehen oder Strg+V drücken
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                      Oder klicke auf den Button unten
                    </p>
                    
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Bild auswählen
                    </button>
                  </div>
                  
                  <div className="text-xs text-gray-400">
                    <p>💡 Tipp: Screenshots können direkt mit Strg+V eingefügt werden!</p>
                  </div>
                </div>
              </div>
              
              {selectedImage && (
                <div className="mt-4">
                  <p className="text-sm text-gray-600 mb-2">
                    Ausgewähltes Bild: {selectedImage.width}x{selectedImage.height}px
                  </p>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <img
                      src={selectedImage.src}
                      alt="Vorschau"
                      className="max-w-full h-auto max-h-48 rounded-lg"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Fehleranzeige */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-red-800">{error}</span>
                </div>
              </div>
            )}

            {/* Generierung */}
            <div className="space-y-4">
              <button
                onClick={() => generateEinblendung()}
                disabled={!selectedImage || isGenerating}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGenerating ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Generiere Einblendung...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Einblendung generieren
                  </>
                )}
              </button>

              {/* Canvas (versteckt) */}
              <canvas
                ref={canvasRef}
                className="hidden"
                width={1920}
                height={1080}
              />

              {/* Generierte Einblendung anzeigen */}
              {isGenerated && canvasRef.current && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Generierte Einblendung:</h4>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex justify-center">
                      <img
                        src={canvasRef.current.toDataURL()}
                        alt="Generierte Einblendung"
                        className="max-w-full h-auto rounded-lg shadow-sm"
                        style={{ maxHeight: '400px' }}
                      />
                    </div>
                    <div className="mt-2 text-center text-sm text-gray-600">
                      Auflösung: 1920x1080px (PNG mit transparentem Hintergrund)
                    </div>
                  </div>
                </div>
              )}

              {/* Download-Button */}
              {isGenerated && canvasRef.current && (
                <button
                  onClick={downloadEinblendung}
                  disabled={isDownloading}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Lade herunter...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Einblendung herunterladen (PNG)
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Info-Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Spezifikationen
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <ul className="list-disc list-inside space-y-1">
                      <li>Canvas: 1920x1080px mit transparentem Hintergrund</li>
                      <li>Bildposition: x=1267px, y=186px</li>
                      <li>Bildbreite: 653px (volle Breite des Zielbereichs)</li>
                      <li>Maximale Bildhöhe: 762px</li>
                      <li>Seitenverhältnis wird immer beibehalten</li>
                      <li>Ausgabe: PNG mit transparentem Hintergrund</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EinblendungJetztPage;
